import React, {FC, useEffect, useMemo, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {
    selectAllEmployees,
    selectBankHolidays,
    selectCalendar,
    selectEvents, selectWarehouseLocks,
} from "../../../features/AppDataSlice";
import {
    selectCalendarVisibleDays,
    selectCalendarFirstDate,
    setCurrentCalendarData,
    setEditCalendarEvent,
    setPreselectedEvent,
    selectCurrentEmployee,
    setPrintCalendarEvent,
    selectDepartment, selectEmployeeCalendarData, setEmployeeCalendarData, selectCalendarData,
} from "../../../features/AppStateSlice";
import CenteredColumn from "../../components/CenteredColumn";
import {getDayOfWeek, showErrorsListInToast, stringToDate, timeToString} from "../../tools";
import {Badge, Spinner} from "react-bootstrap";
import {RRCalendarDateDataType} from "../../types/RRCalendarDataType";
import {getApp} from "firebase/app";
import {getFunctions, httpsCallable} from "firebase/functions";
import {useWarehouseLocks} from "../../warehouse/useWarehouseLocks";
import {RREventType} from "../../types/RREventType";
import {
    LockIconButton,
    PrintIconButton,
    UnlockIconButton,
    XlsIconButton
} from "../../components/IconButtons";
import {RRPrintCalendarEventType} from "../../types/RRPrintCalendarEventType";
import BankHolidayToggle from "../../components/BankHolidayToggle";
import IconButton from "../../components/IconButtons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfo} from "@fortawesome/free-solid-svg-icons";
import EventDayReportsModal from "./EventDayReportsModal";

interface Props {
    onShowEventDetails: (event: RREventType, eventData: RRCalendarDateDataType) => void;
}

const CalendarHeader: FC<Props> = (props) => {
    const dispatch = useAppDispatch();
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const activeDepartment = useAppSelector(selectDepartment);
    const firstDate = useAppSelector(selectCalendarFirstDate);
    const visibleDays = useAppSelector(selectCalendarVisibleDays);
    const events = useAppSelector(selectEvents);
    const calendar = useAppSelector(selectCalendar);
    const allCalendarData = useAppSelector(selectCalendarData);
    const calendarData = useAppSelector(selectEmployeeCalendarData);
    const bankHolidays = useAppSelector(selectBankHolidays);
    const warehouseLocks = useAppSelector(selectWarehouseLocks);
    const warehouseStatus = useWarehouseLocks();
    const allEmployees = useAppSelector(selectAllEmployees);
    // bank holiday toggle is handled by the shared component
    const [locking, setLocking] = useState("");
    const [reportsEvent, setReportsEvent] = useState<{event: RREventType; date: string; department: string} | null>(null);

    useEffect(() => {
        if(currentEmployee && allCalendarData) {
            dispatch(setEmployeeCalendarData(currentEmployee));
        }
    }, [currentEmployee, allCalendarData]);

    const departmentEmployees = useMemo(() => {
        return allEmployees.filter(
            value => (
                (activeDepartment === "store" && value.isStore) ||
                (activeDepartment === "montage" && value.isMontage) ||
                (activeDepartment === "catering" && value.isCatering)
            ) && value.isEmployee
        );
    }, [allEmployees, activeDepartment]);

    useEffect(() => {
        dispatch(setCurrentCalendarData({
            data: calendar.data.filter(
                value => (activeDepartment === value.department)
            ),
            events: events.data.filter(
                value => (
                    (activeDepartment === "store" && value.isStore) ||
                    (activeDepartment === "montage" && value.isMontage) ||
                    (activeDepartment === "catering" && value.isCatering)
                )
            )
        }));
    }, [dispatch, activeDepartment, calendar.data, events, firstDate, visibleDays]);

    function getBGColor(event: RREventType) {
        if (event.id === "WAREHOUSE") return "yellow";
        const eventStatus = event.eventStatus;
        switch (eventStatus) {
            case "НОВО":
                return "cornsilk"
            case "ПОТВЪРДЕНО":
                return "aquamarine"
            case "ОТКАЗАНО":
                return "darkgray"
            case "ПОДГОТВЕНО":
                return "lime"
            case "ЗАВЪРШЕНО":
                return "silver"
        }
        ;
    }

    function editCell(calendarDay: RRCalendarDateDataType,) {
        dispatch(setEditCalendarEvent({
            employee: null,
            calendarDay: calendarDay,
            event: null,
            item: null,
            isReadOnly: false
        }));
    }

    function printCalendarEvent(calendarDay: RRCalendarDateDataType, event: RREventType) {
        const eventForPrinting: RRPrintCalendarEventType = {
            event: event,
            employee: currentEmployee!,
            calendarDay: calendarDay,
            items: calendarDay.items.filter(
                i => i.eventId === event.id
            )
        };
        dispatch(setPrintCalendarEvent(eventForPrinting));
    }

    function exportExcel(event: RREventType, eventData: RRCalendarDateDataType) {
        props.onShowEventDetails && props.onShowEventDetails(event, eventData);
    }

    async function lockWarehouse(date: string) {
        if (!currentEmployee?.isAdmin && !currentEmployee?.isSuperAdmin) {
            showErrorsListInToast("Грешка", ["Нямате права за това!"]);
            return;
        }
        try {
            setLocking(date);
            await httpsCallable(getFunctions(getApp(), "europe-west1"), "setWarehouseLock")({
                date, department: activeDepartment, locked: true,
            });
        } catch (e) {
            showErrorsListInToast("Грешка", [e instanceof Error ? e.message : "Неуспешно заключване."]);
        } finally {
            setLocking("");
        }
    }

    async function unlockWarehouse(date: string) {
        if (!currentEmployee?.isSuperAdmin) {
            showErrorsListInToast("Грешка", ["Нямате права за това!"]);
            return;
        }
        try {
            setLocking(date);
            await httpsCallable(getFunctions(getApp(), "europe-west1"), "setWarehouseLock")({
                date, department: activeDepartment, locked: false,
            });
        } catch (e) {
            showErrorsListInToast("Грешка", [e instanceof Error ? e.message : "Неуспешно отключване."]);
        } finally {
            setLocking("");
        }
    }

    function getEventName(value1: RREventType) {
        if(activeDepartment === "store") {
            return "Кухня";
        }
        return value1.eventShortName || value1.eventNumber;
    }

    const dates = useMemo(() => {
            const result: React.ReactElement[] = [];

            result.push(
                <tr key={"calendar-header-date-row"}>
                    <th>Дата:</th>
                    <th/>
                    {
                        calendarData.map(
                            value => <CenteredColumn header
                                                     colSpan={value.events.length}
                                                     key={value.date}
                                                     className={"td-left-dbl-border text-nowrap"}>
                                <span className={"cursor-pointer"} onClick={(item) => editCell(value)}>
                                    {value.date} ({getDayOfWeek(stringToDate(value.date)!).substring(0, 3)})
                                </span><br/>&nbsp;
                                <BankHolidayToggle departmentKey={activeDepartment} dateStr={value.date} />
                            </CenteredColumn>
                        )
                    }
                </tr>
            );

            result.push(
                <tr key={"calendar-header-events-row"}>
                    <th>Събития:</th>
                    <th/>
                    {
                        calendarData.map(
                            value => {
                                const activeEvents = value.events;

                                return activeEvents.length ?
                                    activeEvents.map(
                                        (value1, idx) => {
                                            let itemCnt = 0;
                                            value.items.forEach(value => {
                                                if (value.eventId === value1.id &&
                                                    value.department === activeDepartment &&
                                                    (
                                                        (value.department === "montage" && value1.isMontage) ||
                                                        (value.department === "catering" && value1.isCatering) ||
                                                        (value.department === "store" && value1.isStore)
                                                    )
                                                ) {
                                                    if (departmentEmployees.findIndex(
                                                        emp => emp.id === value.employeeId
                                                    ) > -1) {
                                                        itemCnt++;
                                                    }
                                                }
                                            });

                                            return <CenteredColumn header key={`${value.date}_${value1.id}`}
                                                                   style={{backgroundColor: getBGColor(value1)}}
                                                                   className={" " +
                                                                       (value1.id === "WAREHOUSE" ? "bg-warehouse" : (
                                                                           idx === 0 ? "td-left-dbl-border" : (
                                                                               value1.eventColor ? value1.eventColor : ""
                                                                           )
                                                                       ))}>
                                                <div className={"link-secondary cursor-pointer text-dark"}

                                                     title={
                                                         (value1.id !== "WAREHOUSE" ?
                                                             `${value1.eventName} [${timeToString(value1.firstDate)}ч., ${value1.eventStatus}]` : "")}>

                                                <span className={"lh-1-2-5em"}
                                                      onClick={() => {
                                                          if (currentEmployee?.isAdmin && value1.id !== "WAREHOUSE") {
                                                              dispatch(setPreselectedEvent(value1))
                                                          }
                                                      }}>
                                                    {getEventName(value1)}<br/>
                                                    {value1.id === "WAREHOUSE" && warehouseStatus(value.date).blocked &&
                                                        <small role="status">{warehouseStatus(value.date).message}<br/></small>}
                                                    <i className={"small"}>{value1.place}</i><br/>
                                                    {itemCnt > 0 ? `(${itemCnt.toString()} сл.)` : ""}
                                                </span>
                                                    {
                                                        (itemCnt > 0) && currentEmployee?.isAdmin &&
                                                        <span><br/>
                                                    <PrintIconButton small
                                                                     onClick={() => printCalendarEvent(value, value1)}/>&nbsp;
                                                            <XlsIconButton small
                                                                           onClick={() => exportExcel(value1, value)}/>&nbsp;
                                                            <IconButton small variant="outline-primary"
                                                                        title="Всички полета на отчетите за деня"
                                                                        icon={<FontAwesomeIcon icon={faInfo}/>}
                                                                        onClick={() => setReportsEvent({event: value1, date: value.date,
                                                                            department: activeDepartment})}/>
                                                </span>
                                                    }
                                                    {
                                                        (value1.id === "WAREHOUSE") && (currentEmployee?.isAdmin || currentEmployee?.isSuperAdmin) &&
                                                        <span>
                                                                {
                                                                    locking === value.date || warehouseStatus(value.date).pending ? <Spinner size="sm"/> :
                                                                        <>{
                                                                            warehouseStatus(value.date).locked ?
                                                                                <LockIconButton
                                                                                    onClick={() => unlockWarehouse(value.date)}/>
                                                                                : warehouseStatus(value.date).blocked ?
                                                                                    <small className="text-danger">{warehouseStatus(value.date).message}</small> :
                                                                                <UnlockIconButton
                                                                                    onClick={() => lockWarehouse(value.date)}/>
                                                                        }</>
                                                                }
                                                            </span>
                                                    }

                                                </div>
                                            </CenteredColumn>
                                        }
                                    ) : <th key={`${value.date}_empty`} className={"td-left-dbl-border"}/>
                            }
                        )
                    }
                </tr>
            );
            return result;
        }
        ,
        [calendarData, bankHolidays, locking, warehouseLocks, warehouseStatus, activeDepartment, currentEmployee, departmentEmployees]
    );

    return (
        <>
        <thead style={{zIndex: 1000}}>
        {dates}
        </thead>
        {reportsEvent && <EventDayReportsModal {...reportsEvent} onClose={() => setReportsEvent(null)}/>}
        </>
    )
}

export default CalendarHeader;
